import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { SectionHelpTooltip } from "@/components/shared/SectionHelpTooltip";
import { useToast } from "@/hooks/use-toast";
import {
  notificationsApi,
  pickLang,
  type NotificationAdmin,
  type SendMode,
} from "@/services/notifications-api.service";

export default function NotificationsAdmin() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [tab, setTab] = useState<"list" | "templates">("list");

  const scopes = useQuery({
    queryKey: ["admin-notify-scopes"],
    queryFn: () => notificationsApi.scopes(),
  });

  const list = useQuery({
    queryKey: ["admin-notifications"],
    queryFn: () => notificationsApi.adminList({ limit: 50 }),
  });

  const templates = useQuery({
    queryKey: ["admin-notify-templates"],
    queryFn: () => notificationsApi.templatesList(),
    enabled: tab === "templates",
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [titleRu, setTitleRu] = useState("");
  const [bodyRu, setBodyRu] = useState("");
  const [scope, setScope] = useState("general");
  const [sendAt, setSendAt] = useState("");

  const [sendTarget, setSendTarget] = useState<NotificationAdmin | null>(null);
  const [sendMode, setSendMode] = useState<SendMode>("all");
  const [recipientQ, setRecipientQ] = useState("");
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

  const [deleteId, setDeleteId] = useState<number | null>(null);

  const [tplSlug, setTplSlug] = useState("");
  const [tplTitle, setTplTitle] = useState("");
  const [tplBody, setTplBody] = useState("");
  const [tplScope, setTplScope] = useState("general");
  const [tplCreateOpen, setTplCreateOpen] = useState(false);

  const recipients = useQuery({
    queryKey: ["admin-notify-recipients", recipientQ],
    queryFn: () => notificationsApi.recipients(recipientQ),
    enabled: !!sendTarget && sendMode === "users" && recipientQ.length >= 1,
  });

  const createMut = useMutation({
    mutationFn: () =>
      notificationsApi.adminCreate({
        type: "admin",
        title: { ru: titleRu },
        body: { ru: bodyRu },
        scope,
        send_at: sendAt ? new Date(sendAt).toISOString() : null,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin-notifications"] });
      setCreateOpen(false);
      setTitleRu("");
      setBodyRu("");
      toast({ title: "Черновик создан" });
    },
  });

  const sendMut = useMutation({
    mutationFn: async () => {
      if (!sendTarget) return;
      return notificationsApi.adminSend(sendTarget.id, {
        mode: sendMode,
        user_ids: sendMode === "users" ? selectedUsers : [],
        role_ids: [],
      });
    },
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ["admin-notifications"] });
      setSendTarget(null);
      toast({
        title: res?.deferred ? "Отложено" : "Поставлено в очередь",
        description: res
          ? `батчей: ${res.queued_batches}, получателей: ${res.recipient_count}`
          : undefined,
      });
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => notificationsApi.adminDelete(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin-notifications"] });
      setDeleteId(null);
      toast({ title: "Удалено" });
    },
  });

  const tplCreateMut = useMutation({
    mutationFn: () =>
      notificationsApi.templatesCreate({
        slug: tplSlug,
        title: { ru: tplTitle },
        body: { ru: tplBody },
        scope: tplScope,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin-notify-templates"] });
      setTplCreateOpen(false);
      toast({ title: "Шаблон создан" });
    },
  });

  const statusBadge = useMemo(
    () =>
      ({
        draft: "secondary",
        processing: "default",
        sent: "outline",
        archived: "secondary",
      }) as const,
    [],
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Bell className="h-5 w-5" />
        <h2 className="flex items-center gap-2 text-lg font-semibold">
          Уведомления
          <SectionHelpTooltip label="Справка: админ уведомлений">
            <p>Рассылки и шаблоны серверных уведомлений.</p>
            <ul className="mt-1 list-disc space-y-1 pl-4">
              <li>Создайте draft → Send с адресностью (всем / пользователи)</li>
              <li>Отложенная отправка — поле send_at</li>
              <li>
                Если статус завис в processing без доставки — «Отправить» повторит claim
              </li>
              <li>Удаление — только не processing (диалог подтверждения)</li>
              <li>Шаблоны: slug + {"{{var}}"}, preview через render API</li>
            </ul>
          </SectionHelpTooltip>
        </h2>
        <div className="ml-auto flex gap-2">
          <Button
            size="sm"
            variant={tab === "list" ? "default" : "outline"}
            onClick={() => setTab("list")}
          >
            Рассылки
          </Button>
          <Button
            size="sm"
            variant={tab === "templates" ? "default" : "outline"}
            onClick={() => setTab("templates")}
          >
            Шаблоны
          </Button>
        </div>
      </div>

      {tab === "list" && (
        <>
          <div className="flex justify-end">
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              Создать
            </Button>
          </div>
          <div className="space-y-2">
            {(list.data ?? []).map((n) => (
              <div
                key={n.id}
                className="flex flex-wrap items-center gap-2 rounded-md border px-3 py-2"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{pickLang(n.title)}</span>
                    <Badge variant={statusBadge[n.status]}>{n.status}</Badge>
                    <span className="text-xs text-muted-foreground">{n.scope}</span>
                  </div>
                  <p className="line-clamp-1 text-sm text-muted-foreground">
                    {pickLang(n.body)}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={n.status === "sent"}
                  title={
                    n.status === "processing"
                      ? "Повтор, если доставка не началась"
                      : undefined
                  }
                  onClick={() => {
                    setSendTarget(n);
                    setSendMode("all");
                    setSelectedUsers([]);
                  }}
                >
                  {n.status === "processing" ? "Повторить" : "Отправить"}
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  disabled={n.status === "processing"}
                  onClick={() => setDeleteId(n.id)}
                >
                  Удалить
                </Button>
              </div>
            ))}
            {!list.isLoading && (list.data ?? []).length === 0 && (
              <p className="text-sm text-muted-foreground">Пока нет рассылок</p>
            )}
          </div>
        </>
      )}

      {tab === "templates" && (
        <>
          <div className="flex justify-end">
            <Button size="sm" onClick={() => setTplCreateOpen(true)}>
              Новый шаблон
            </Button>
          </div>
          <div className="space-y-2">
            {(templates.data ?? []).map((t) => (
              <div key={t.id} className="rounded-md border px-3 py-2">
                <div className="font-medium">
                  {t.slug} · {pickLang(t.title)}
                </div>
                <p className="text-sm text-muted-foreground">{pickLang(t.body)}</p>
              </div>
            ))}
          </div>
        </>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="z-[230]">
          <DialogHeader>
            <DialogTitle>Новое уведомление</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Заголовок (ru)</Label>
              <Input value={titleRu} onChange={(e) => setTitleRu(e.target.value)} />
            </div>
            <div>
              <Label>Текст (ru)</Label>
              <Textarea value={bodyRu} onChange={(e) => setBodyRu(e.target.value)} rows={4} />
            </div>
            <div>
              <Label>Scope</Label>
              <Select value={scope} onValueChange={setScope}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[250]">
                  {(scopes.data ?? [{ code: "general", label: "Новости" }]).map((s) => (
                    <SelectItem key={s.code} value={s.code}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Отложить (необязательно)</Label>
              <Input
                type="datetime-local"
                value={sendAt}
                onChange={(e) => setSendAt(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Отмена
            </Button>
            <Button
              disabled={!titleRu.trim() || !bodyRu.trim() || createMut.isPending}
              onClick={() => createMut.mutate()}
            >
              Создать draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!sendTarget} onOpenChange={(o) => !o && setSendTarget(null)}>
        <DialogContent className="z-[230]">
          <DialogHeader>
            <DialogTitle>Отправка уведомления</DialogTitle>
          </DialogHeader>
          <RadioGroup
            value={sendMode}
            onValueChange={(v) => setSendMode(v as SendMode)}
            className="space-y-2"
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem value="all" id="mode-all" />
              <Label htmlFor="mode-all">Всем активным</Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="users" id="mode-users" />
              <Label htmlFor="mode-users">Выбранные пользователи</Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="roles" id="mode-roles" />
              <Label htmlFor="mode-roles">По ролям (укажите role_ids позже / через API)</Label>
            </div>
          </RadioGroup>
          {sendMode === "users" && (
            <div className="space-y-2">
              <Input
                placeholder="Поиск пользователей…"
                value={recipientQ}
                onChange={(e) => setRecipientQ(e.target.value)}
              />
              <div className="max-h-40 space-y-1 overflow-y-auto">
                {(recipients.data ?? []).map((u) => {
                  const checked = selectedUsers.includes(u.id);
                  return (
                    <label key={u.id} className="flex cursor-pointer items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() =>
                          setSelectedUsers((prev) =>
                            checked ? prev.filter((x) => x !== u.id) : [...prev, u.id],
                          )
                        }
                      />
                      {u.email || u.username || u.id}
                    </label>
                  );
                })}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendTarget(null)}>
              Отмена
            </Button>
            <Button disabled={sendMut.isPending} onClick={() => sendMut.mutate()}>
              Отправить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteId != null} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="z-[230]">
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить уведомление?</AlertDialogTitle>
            <AlertDialogDescription>
              Запись и связанные строки ленты будут удалены. Статус processing пропускается.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteId != null && deleteMut.mutate(deleteId)}
            >
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={tplCreateOpen} onOpenChange={setTplCreateOpen}>
        <DialogContent className="z-[230]">
          <DialogHeader>
            <DialogTitle>Шаблон уведомления</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Slug</Label>
              <Input value={tplSlug} onChange={(e) => setTplSlug(e.target.value)} />
            </div>
            <div>
              <Label>Заголовок (ru), можно {"{{var}}"}</Label>
              <Input value={tplTitle} onChange={(e) => setTplTitle(e.target.value)} />
            </div>
            <div>
              <Label>Текст (ru)</Label>
              <Textarea value={tplBody} onChange={(e) => setTplBody(e.target.value)} />
            </div>
            <div>
              <Label>Scope</Label>
              <Select value={tplScope} onValueChange={setTplScope}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[250]">
                  {(scopes.data ?? [{ code: "general", label: "Новости" }]).map((s) => (
                    <SelectItem key={s.code} value={s.code}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTplCreateOpen(false)}>
              Отмена
            </Button>
            <Button
              disabled={!tplSlug || !tplTitle || !tplBody || tplCreateMut.isPending}
              onClick={() => tplCreateMut.mutate()}
            >
              Создать
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
